import { supabase } from '../lib/supabase';

class EquipmentService {
  // Get all equipment (no auth check)
  async getAllEquipment() {
    try {
      const { data, error } = await supabase
        ?.from('equipment')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      
      // Convert snake_case to camelCase
      const formattedData = data?.map(equipment => ({
        id: equipment?.id,
        productionAreaId: equipment?.production_area_id,
        name: equipment?.name,
        operational: equipment?.operational,
        equipmentType: equipment?.equipment_type,
        powerRequirements: equipment?.power_requirements,
        costPerUnit: equipment?.cost_per_unit,
        unitOfMeasure: equipment?.unit_of_measure,
        availabilityStatus: equipment?.availability_status,
        availabilityCalendar: equipment?.availability_calendar,
        createdAt: equipment?.created_at,
        updatedAt: equipment?.updated_at
      }));
      
      return { data: formattedData, error: null };
    } catch (error) {
      console.error('Error fetching equipment:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get equipment by ID (no auth check)
  async getEquipmentById(id) {
    try {
      const { data, error } = await supabase
        ?.from('equipment')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (error) throw error;
      
      // Convert snake_case to camelCase
      const formattedData = {
        id: data?.id,
        productionAreaId: data?.production_area_id,
        name: data?.name,
        operational: data?.operational,
        equipmentType: data?.equipment_type,
        powerRequirements: data?.power_requirements,
        costPerUnit: data?.cost_per_unit,
        unitOfMeasure: data?.unit_of_measure,
        availabilityStatus: data?.availability_status,
        availabilityCalendar: data?.availability_calendar,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
      
      return { data: formattedData, error: null };
    } catch (error) {
      console.error('Error fetching equipment:', error);
      return { data: null, error: error?.message };
    }
  }

  // Create new equipment (no auth check)
  async createEquipment(equipmentData) {
    try {
      const { data, error } = await supabase
        ?.from('equipment')
        ?.insert([{
          production_area_id: equipmentData?.productionAreaId,
          name: equipmentData?.name,
          operational: equipmentData?.operational ?? true,
          equipment_type: equipmentData?.equipmentType,
          power_requirements: equipmentData?.powerRequirements,
          cost_per_unit: equipmentData?.costPerUnit,
          unit_of_measure: equipmentData?.unitOfMeasure || 'unit',
          availability_status: equipmentData?.availabilityStatus || 'available',
          availability_calendar: equipmentData?.availabilityCalendar || []
        }])
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating equipment:', error);
      return { data: null, error: error?.message };
    }
  }

  // Update equipment (no auth check)
  async updateEquipment(id, equipmentData) {
    try {
      const updateData = {
        production_area_id: equipmentData?.productionAreaId,
        name: equipmentData?.name,
        operational: equipmentData?.operational,
        equipment_type: equipmentData?.equipmentType,
        power_requirements: equipmentData?.powerRequirements,
        cost_per_unit: equipmentData?.costPerUnit,
        unit_of_measure: equipmentData?.unitOfMeasure,
        availability_status: equipmentData?.availabilityStatus,
        availability_calendar: equipmentData?.availabilityCalendar,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase
        ?.from('equipment')
        ?.update(updateData)
        ?.eq('id', id)
        ?.select();

      if (error) throw error;
      
      if (!data || data?.length === 0) {
        throw new Error('Equipment not found or no changes made');
      }
      
      return { data: data?.[0], error: null };
    } catch (error) {
      console.error('Error updating equipment:', error);
      return { data: null, error: error?.message };
    }
  }

  // Delete equipment by ID (no auth check)
  async deleteEquipment(id) {
    try {
      const { error } = await supabase
        ?.from('equipment')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      
      return { data: { id }, error: null };
    } catch (error) {
      console.error('Error deleting equipment:', error);
      return { data: null, error: error?.message };
    }
  }

  // Search equipment by query (no auth check)
  async searchEquipment(query) {
    try {
      const { data, error } = await supabase
        ?.from('equipment')
        ?.select('*')
        ?.or(`name.ilike.%${query}%,equipment_type.ilike.%${query}%`)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      
      // Convert snake_case to camelCase
      const formattedData = data?.map(equipment => ({
        id: equipment?.id,
        productionAreaId: equipment?.production_area_id,
        name: equipment?.name,
        operational: equipment?.operational,
        equipmentType: equipment?.equipment_type,
        powerRequirements: equipment?.power_requirements,
        costPerUnit: equipment?.cost_per_unit,
        unitOfMeasure: equipment?.unit_of_measure,
        availabilityStatus: equipment?.availability_status,
        availabilityCalendar: equipment?.availability_calendar,
        createdAt: equipment?.created_at,
        updatedAt: equipment?.updated_at
      }));
      
      return { data: formattedData, error: null };
    } catch (error) {
      console.error('Error searching equipment:', error);
      return { data: null, error: error?.message };
    }
  }
}

export default new EquipmentService();